import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Wrench } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";

// Seules les pages de la boutique sont bloquées quand elle est fermée ;
// tout le reste du site (accueil, catalogues, blogue, contact…) reste ouvert.
const BLOCKED = ["/boutique", "/produit", "/panier", "/commande"];
const REDIRECT_SECONDS = 4;

export default function MaintenanceGate({ children }) {
  const { t } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const [closed, setClosed] = useState(false);
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);

  useEffect(() => {
    api.get("/settings/shop").then((r) => setClosed(r.data.shop_open === false)).catch(() => {});
  }, []);

  const blocked = closed &&
    BLOCKED.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));

  useEffect(() => {
    setCountdown(REDIRECT_SECONDS);
    if (!blocked) return undefined;
    const timer = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(timer);
          navigate("/catalogues", { replace: true });
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [blocked, location.pathname, navigate]);

  if (!blocked) return children;

  return (
    <div data-testid="maintenance-page" className="fixed inset-0 z-[100] bg-ink flex flex-col items-center justify-center px-6 text-center">
      <div className="w-20 h-20 rounded-full border border-gold/30 bg-gold/10 flex items-center justify-center mb-8">
        <Wrench className="w-9 h-9 text-gold" />
      </div>
      <h1 className="font-serif text-5xl md:text-6xl mb-4">{t("maintenance.title")}</h1>
      <p className="text-ivory/60 max-w-md mb-10">{t("maintenance.subtitle")}</p>
      <div className="w-56 h-1 bg-ivory/10 rounded-full overflow-hidden mb-3">
        <div
          className="h-full bg-gold transition-[width] duration-1000 ease-linear"
          style={{ width: `${(countdown / REDIRECT_SECONDS) * 100}%` }}
          data-testid="maintenance-progress"
        />
      </div>
      <p className="text-sm text-ivory/70 mb-10" data-testid="maintenance-countdown">
        {t("maintenance.redirect")} <span className="text-gold font-semibold">{countdown}</span> s
      </p>
      <Link
        to="/catalogues" data-testid="maintenance-catalogues-link"
        className="inline-flex items-center gap-3 bg-gold text-ink font-semibold px-8 py-4 rounded-full hover:bg-champagne transition-colors duration-300"
      >
        {t("maintenance.cta_now")}
      </Link>
    </div>
  );
}
